import { useEffect } from "react";
import axios, { API, isServerDown } from "@/lib/api";
import { cacheData, loadCachedData, CACHE_KEYS } from "@/utils/offlineCache";

// 🔥 Background cache warmer: every app open (POS or admin, any user) pulls the data the store
// needs to keep working offline — products (delta: only what changed), stores, categories, rates,
// settings, customers, combos… — so the tenant never has to "visit a page first" to have it locally.
const LAST_KEY = "sellox_cache_warm_at";
const EVERY_MS = 30 * 60 * 1000;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function storeQuery() {
  try { const raw = sessionStorage.getItem("pos_store") || localStorage.getItem("pos_store"); const id = raw ? (JSON.parse(raw) || {}).id : null; return id ? `?store_id=${id}` : ""; } catch { return ""; }
}

async function warmProducts() {
  if (window._productRefreshInterval) return "pos-owns-it"; // POS page already keeps the catalog fresh
  const sq = storeQuery(); const key = "pos_products_sync_" + (sq || "all");
  const last = localStorage.getItem(key) || "";
  const cached = await loadCachedData(CACHE_KEYS.PRODUCTS, []);
  if (last && cached.length && Date.now() - new Date(last).getTime() < 12 * 3600 * 1000) {
    const r = await axios.get(`${API}/products/pos${sq}${sq ? "&" : "?"}since=${encodeURIComponent(last)}`, { skipGlobalError: true });
    const changed = r.data?.items || [];
    if (changed.length) {
      const map = new Map(cached.map((p) => [p.id, p]));
      for (const p of changed) { if (p.status && p.status !== "active") map.delete(p.id); else map.set(p.id, p); }
      await cacheData(CACHE_KEYS.PRODUCTS, [...map.values()]);
    }
    if (r.data?.server_time) localStorage.setItem(key, r.data.server_time);
    return `delta ${changed.length}`;
  }
  const r = await axios.get(`${API}/products/pos${sq}`, { skipGlobalError: true, timeout: 60000 });
  if (Array.isArray(r.data)) { await cacheData(CACHE_KEYS.PRODUCTS, r.data); localStorage.setItem(key, new Date().toISOString()); }
  return `full ${Array.isArray(r.data) ? r.data.length : 0}`;
}

const posStores = (list) => { const all = (list || []).filter((s) => (s.status || "active") === "active"); const r = all.filter((s) => s.type === "retail"); return r.length ? r : all; };

const SMALL = [
  ["/stores", CACHE_KEYS.STORES, posStores],
  ["/categories", CACHE_KEYS.CATEGORIES],
  ["/exchange-rates", CACHE_KEYS.EXCHANGE_RATES],
  ["/settings/system", CACHE_KEYS.SETTINGS],
  ["/warehouses", CACHE_KEYS.WAREHOUSES],
  ["/customers", CACHE_KEYS.CUSTOMERS],
  ["/bundles/pos", CACHE_KEYS.COMBOS],
  // admin pages: just fetching stores them in the universal GET cache
  ["/suppliers"], ["/employees"], ["/products/brands"], ["/promotions?status=active"], ["/tier-pricing?status=active"], ["/industry/status"],
];

export async function warmCache(reason) {
  if (!navigator.onLine || isServerDown()) return;
  const out = [];
  for (const [path, key, map] of SMALL) {
    try {
      const r = await axios.get(`${API}${path}`, { skipGlobalError: true });
      if (key) await cacheData(key, map ? map(r.data) : r.data);
      out.push(path.split("?")[0]);
    } catch (e) { console.warn("[cache-warm] skip", path, e?.message); }
    await sleep(150);
  }
  try { out.push("products:" + (await warmProducts())); } catch (e) { out.push("products:fail"); }
  localStorage.setItem(LAST_KEY, String(Date.now()));
  console.info(`[cache-warm] ${reason}:`, out.join(" "));
}

export function CacheWarmer() {
  useEffect(() => {
    let timer = null;
    const run = (why) => { if (localStorage.getItem("token") || localStorage.getItem("pos_token")) warmCache(why).catch(() => {}); };
    const first = setTimeout(() => {
      const last = Number(localStorage.getItem(LAST_KEY) || 0);
      if (Date.now() - last > 5 * 60 * 1000) run("open");
      timer = setInterval(() => run("periodic"), EVERY_MS);
    }, 8000);
    const onOnline = () => setTimeout(() => run("back-online"), 5000);
    window.addEventListener("online", onOnline);
    return () => { clearTimeout(first); if (timer) clearInterval(timer); window.removeEventListener("online", onOnline); };
  }, []);
  return null;
}
